/**
 * Public API types for Data Notebook
 */

import type { DataNotebookConfig } from './config';

/**
 * Query result from DuckDB
 */
export interface QueryResult {
  /** Result columns */
  columns: string[];
  /** Result rows */
  rows: any[][];
  /** Number of rows returned */
  rowCount: number;
  /** Query execution time in milliseconds */
  executionTime?: number;
}

/**
 * Table metadata
 */
export interface Table {
  /** Table name */
  name: string;
  /** Number of rows */
  rowCount: number;
  /** Number of columns */
  columnCount: number;
  /** Column definitions */
  columns: ColumnInfo[];
  /** Data size in bytes */
  sizeBytes: number;
  /** Source of the data */
  source: DataSource;
  /** Timestamp when loaded */
  timestamp: number;
}

/**
 * Column information
 */
export interface ColumnInfo {
  /** Column name */
  name: string;
  /** Column type */
  type: string;
  /** Is nullable */
  nullable: boolean;
}

/**
 * Data source information
 */
export interface DataSource {
  /** Source type */
  type: 'network' | 'blob' | 'manual';
  /** Original URL (for network sources) */
  url?: string;
  /** Original filename */
  filename?: string;
  /** HTTP method (for network sources) */
  method?: string;
  /** Content type */
  contentType?: string;
}

/**
 * CSV interception event
 */
export interface CSVInterceptEvent {
  /** CSV data */
  data: string;
  /** Data source */
  source: DataSource;
  /** Size in bytes */
  size: number;
  /** Suggested table name */
  suggestedName?: string;
  /** Timestamp */
  timestamp: number;
}

/**
 * Public API interface
 */
export interface DataNotebookAPI {
  // ===== Lifecycle =====

  /** Initialize the overlay panel */
  init(config?: DataNotebookConfig): Promise<void>;

  /** Destroy the overlay panel and clean up resources */
  destroy(): Promise<void>;

  // ===== Panel Control =====

  /** Show the panel */
  show(): void;

  /** Hide the panel */
  hide(): void;

  /** Toggle panel visibility */
  toggle(): void;

  /** Minimize the panel */
  minimize(): void;

  /** Expand the panel */
  expand(): void;

  // ===== Database Operations =====

  /** Load CSV data into DuckDB */
  loadCSV(data: string, name?: string): Promise<void>;

  /** Execute a SQL query */
  query(sql: string): Promise<QueryResult>;

  /** Get all tables */
  getTables(): Promise<Table[]>;

  /** Get table metadata */
  getTable(name: string): Promise<Table | null>;

  /** Drop a table */
  dropTable(name: string): Promise<void>;

  /** Clear all tables */
  clearAll(): Promise<void>;

  // ===== Event System =====

  /** Register event listener */
  on(event: string, handler: (...args: any[]) => void): void;

  /** Unregister event listener */
  off(event: string, handler: (...args: any[]) => void): void;

  /** Register one-time event listener */
  once(event: string, handler: (...args: any[]) => void): void;

  // ===== Utilities =====

  /** Export table to CSV */
  exportCSV(tableName: string): Promise<Blob>;

  /** Export table to JSON */
  exportJSON(tableName: string): Promise<Blob>;

  /** Get current configuration */
  getConfig(): Required<DataNotebookConfig>;

  /** Update configuration */
  updateConfig(config: Partial<DataNotebookConfig>): void;
}

/**
 * Event names
 */
export enum DataNotebookEvent {
  // Lifecycle events
  INITIALIZED = 'initialized',
  DESTROYED = 'destroyed',

  // Panel events
  PANEL_SHOWN = 'panel:shown',
  PANEL_HIDDEN = 'panel:hidden',
  PANEL_MINIMIZED = 'panel:minimized',
  PANEL_EXPANDED = 'panel:expanded',

  // Data events
  CSV_INTERCEPTED = 'data:csv-intercepted',
  TABLE_LOADED = 'data:table-loaded',
  TABLE_DROPPED = 'data:table-dropped',

  // Query events
  QUERY_STARTED = 'query:started',
  QUERY_COMPLETED = 'query:completed',
  QUERY_FAILED = 'query:failed',

  // Error events
  ERROR = 'error',
  WARNING = 'warning',
}

/**
 * Error types
 */
export class DataNotebookError extends Error {
  constructor(
    message: string,
    public code: string,
    public details?: any
  ) {
    super(message);
    this.name = 'DataNotebookError';
  }
}
